import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import posthog from "posthog-js";
import { Button } from "../../components/Button";
import { api } from "../../services/api";

type Cohort = {
    key: string;
    name: string;
};

function CohortItem({ name, onClick, selected }: { name: string, onClick: () => void, selected: boolean }) {
    return (
        <div role="button" className={`self-stretch p-4 opacity-80  rounded-lg outline outline-1 outline-offset-[-1px] ${selected ? 'outline-primary bg-background2' : 'outline-gray2 bg-transparent'} flex justify-start items-center gap-2.5`} onClick={onClick}>
            <div className="justify-start text-primary text-xl font-semibold leading-normal">
                {name}
            </div>
        </div>
    );
}

export function CohortForm({ onContinue }: { onContinue: () => void }) {
    const [selectedCohort, setSelectedCohort] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const { data: cohorts, isLoading, isError } = useQuery<Cohort[]>({
        queryKey: ["onboardingGroups"],
        queryFn: () => api.getOnboardingGroups().then((res) => res.data?.groups ?? []),
        // This is a one-shot fetch for an ephemeral onboarding step: no retry
        // (fail fast into the skip effect below) and no background refetch
        // (staleTime: Infinity blocks refetchOnWindowFocus/Reconnect/Mount), so
        // a later transient failure can never silently replace an already-loaded
        // cohort list with nothing while the user is mid-selection.
        retry: false,
        staleTime: Infinity,
    });

    const handleContinue = async () => {
        if (selectedCohort) {
            try {
                setSaving(true);
                await api.assignGroup({ group: selectedCohort });
                // identify() already ran at login before a group existed.
                posthog.setPersonProperties({ group: selectedCohort });
                onContinue();
                setSaving(false);
            } catch (error) {
                console.error(error);
                setSaving(false);
            }
        }
    }

    useEffect(() => {
        if (cohorts?.length === 1) {
            setSelectedCohort(cohorts[0].key);
        }
    }, [cohorts]);

    // Skip this step when there's nothing usable to show: either the fetch
    // failed outright (isError, cohorts stays undefined) or it succeeded with
    // an empty list (every cohort is hidden from this selector). Otherwise the
    // user is stranded on an empty list with a permanently disabled Continue
    // button.
    useEffect(() => {
        if (!isLoading && (isError || cohorts?.length === 0)) {
            onContinue();
        }
    }, [isLoading, isError, cohorts, onContinue]);

    return <div>
        <div className="flex flex-col gap-6 max-h-[275px] overflow-y-auto pr-3">
            {cohorts?.map((cohort) => (
                <CohortItem key={cohort.key} name={cohort.name} onClick={() => setSelectedCohort(cohort.key)} selected={selectedCohort === cohort.key} />
            ))}
        </div>
        <div className="flex mt-11 mr-3">
            <Button variant="primary" disabled={saving || isLoading || !selectedCohort} loading={saving} className="w-full !rounded-full" onClick={handleContinue}>Continue</Button>
        </div>
    </div>;
}